import { config } from "@/shared/lib/config";
import { fetcher, handleError } from "@/shared/lib/fetcher";

const base = () => `${config.apiUrl}/performance`;

const withSortQuery = (
  path: string,
  params?: { sortBy?: string; sortOrder?: "asc" | "desc" },
) => {
  const qp = new URLSearchParams();
  if (params?.sortBy) qp.set("sortBy", params.sortBy);
  if (params?.sortOrder) qp.set("sortOrder", params.sortOrder);
  const query = qp.toString();
  return query ? `${path}?${query}` : path;
};

export type AssignedTaskAlertStatusDTO = "none" | "due_soon" | "overdue";

export type AssignedMeetingTaskDTO = {
  id: string;
  source: "meeting";
  title: string;
  meetingId: string;
  meetingTitle: string;
  dueDate: string | null;
  status: "pending" | "done";
  /** ກອງປະຊຸມບໍ່ມີຄວາມສຳຄັນໃນ DB */
  priority: null;
  alertStatus: AssignedTaskAlertStatusDTO;
};

export type AssignedProjectTaskDTO = {
  id: string;
  source: "project";
  title: string;
  projectId: string;
  projectName: string;
  dueDate: string | null;
  status: "pending" | "in_progress" | "done";
  /** % ຄວາມຄືບໜ້າ (0–100), ສະເພາະວຽກໂຄງການ */
  progressPercent: number | null;
  priority: "high" | "medium" | "low" | null;
  alertStatus: AssignedTaskAlertStatusDTO;
};

export type AssignedTaskDTO = AssignedMeetingTaskDTO | AssignedProjectTaskDTO;

export type PerformanceTaskNotificationDTO = {
  id: string;
  source: "meeting" | "project";
  title: string;
  alertStatus: "overdue" | "due_soon";
  dueDate: string | null;
  meetingId?: string;
  projectId?: string;
};

export type PerformanceHubSummaryDTO = {
  kpi: {
    count: number;
    personalCount: number;
    departmentCount: number;
    passCount: number;
    failCount: number;
  };
  tasks: { total: number; pending: number; overdue: number; dueSoon: number };
  /** ຄ້າຍ checklistAlerts — ຈຳນວນວຽກຄ້າງທີ່ເກີນ/ໃກ້ຄົບກຳນົດ */
  taskAlerts: { overdue: number; dueSoon: number };
  taskNotifications: PerformanceTaskNotificationDTO[];
  preview: { tasks: AssignedTaskDTO[] };
};

export type PerformanceKpiPeriodResultDTO = {
  periodYear: number;
  periodMonth: number | null;
  periodQuarter: number | null;
  actualValue: number;
  targetValue: number | null;
  status: "pass" | "fail" | "pending";
  measuredAt: string;
};

export type ProjectMyTaskRowDTO = {
  id: string;
  title: string;
  dueDate: string | null;
  status: "pending" | "in_progress" | "done";
  priority: "high" | "medium" | "low" | null;
  progressPercent: number | null;
  alertStatus: AssignedTaskAlertStatusDTO;
};

export type ProjectMyTasksDTO = {
  project: {
    id: string;
    name: string;
    status: "active" | "completed" | "on_hold";
  };
  tasks: ProjectMyTaskRowDTO[];
};

export type PerformanceKpiItemDTO = {
  assignmentId: string;
  kpiId: string;
  kpiScope: "personal" | "department";
  name: string;
  measurementPeriod: "monthly" | "quarterly" | "yearly";
  targetValue: number | null;
  targetUnit: "percent" | "count" | "score" | "currency" | "none" | null;
  currentValue: number | null;
  currentStatus: "pass" | "fail" | "pending" | null;
  measuredAt: string | null;
  periodResults: PerformanceKpiPeriodResultDTO[];
};

export type PerformanceKpiListMetaDTO = {
  total: number;
  passCount: number;
  failCount: number;
  pendingCount: number;
};

export type PerformanceKpiPeriodGroupRowDTO = {
  periodLabel: string;
  actualValue: number;
  targetValue: number | null;
  status: "pass" | "fail" | "pending";
  measuredAt: string;
};

export type PerformanceKpiPeriodGroupDTO = {
  kpiName: string;
  scopeLabel: string;
  targetUnit: PerformanceKpiItemDTO["targetUnit"];
  rows: PerformanceKpiPeriodGroupRowDTO[];
};

/** ລາຍລະອຽດ KPI ຕາມ assignment (ມອບໝາຍຕໍ່ຜູ້ໃຊ້ເທົ່ານັ້ນ) */
export type PerformanceKpiDetailDTO = PerformanceKpiItemDTO & {
  description: string | null;
  measurementMethod: string;
  measurementSource: string | null;
  passCriteria: string | null;
  failCriteria: string | null;
  notes: string | null;
  kpiLevel: "employee" | "position" | "department" | "organization";
  assignmentType: "employee" | "position" | "department";
  startDate: string;
  endDate: string | null;
};

export const performanceApi = {
  hub(): Promise<PerformanceHubSummaryDTO> {
    return fetcher.get(`${base()}/hub`);
  },
  meKpi(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    limit?: number;
    offset?: number;
    q?: string;
  }): Promise<{
    data: PerformanceKpiItemDTO[];
    meta: PerformanceKpiListMetaDTO;
  }> {
    const qp = new URLSearchParams();
    if (params?.sortBy) qp.set("sortBy", params.sortBy);
    if (params?.sortOrder) qp.set("sortOrder", params.sortOrder);
    if (params?.limit != null) qp.set("limit", String(params.limit));
    if (params?.offset != null) qp.set("offset", String(params.offset));
    if (params?.q) qp.set("q", params.q);
    const query = qp.toString();
    return fetcher.get(
      query ? `${base()}/me/kpi?${query}` : `${base()}/me/kpi`,
    );
  },
  meKpiPeriodGroups(params?: {
    limit?: number;
    offset?: number;
  }): Promise<{
    data: PerformanceKpiPeriodGroupDTO[];
    meta: { total: number };
  }> {
    const qp = new URLSearchParams();
    if (params?.limit != null) qp.set("limit", String(params.limit));
    if (params?.offset != null) qp.set("offset", String(params.offset));
    const query = qp.toString();
    return fetcher.get(
      query
        ? `${base()}/me/kpi/period-groups?${query}`
        : `${base()}/me/kpi/period-groups`,
    );
  },
  meKpiDetail(assignmentId: string): Promise<{ data: PerformanceKpiDetailDTO }> {
    return fetcher.get(
      `${base()}/me/kpi/${encodeURIComponent(assignmentId)}`,
    );
  },
  meTasks(params?: {
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{ data: AssignedTaskDTO[] }> {
    return fetcher.get(withSortQuery(`${base()}/me/tasks`, params));
  },
  patchMeetingAssignedTask(
    id: string,
    body: { status: "pending" | "done" },
  ): Promise<{ data: AssignedMeetingTaskDTO }> {
    return fetcher.patch(
      `${base()}/me/tasks/meeting/${encodeURIComponent(id)}`,
      body,
    );
  },
  patchProjectAssignedTask(
    id: string,
    body: {
      status?: "pending" | "in_progress" | "done";
      progressPercent?: number | null;
    },
  ): Promise<{ data: AssignedProjectTaskDTO }> {
    return fetcher.patch(
      `${base()}/me/tasks/project/${encodeURIComponent(id)}`,
      body,
    );
  },
  /** ຄືນ null ຖ້າ 404 (ບໍ່ມີວຽກຂອງທ່ານໃນໂຄງການນີ້). */
  async meTasksInProject(projectId: string): Promise<ProjectMyTasksDTO | null> {
    const url = `${base()}/me/projects/${encodeURIComponent(projectId)}/my-tasks`;
    const res = await fetch(url, { credentials: "include" });
    if (res.status === 404) return null;
    if (!res.ok) return handleError(res);
    return (await res.json()) as ProjectMyTasksDTO;
  },
};
